import type { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { SideNavItem } from './side-nav-item'
import { Item } from '../config'
import { SIDE_NAV_SECTION_TITLE_FONT_SIZE } from './side-nav-layout-dimensions'

const renderItems = ({
  depth = 0,
  items,
  pathname,
  isFolded,
  labelsVisible,
}: {
  depth?: number
  items: Item[]
  pathname?: string | null
  isFolded?: boolean
  labelsVisible?: boolean
}): JSX.Element[] =>
  items.reduce(
    (acc: JSX.Element[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
        isFolded,
        labelsVisible,
      }),
    []
  )

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  isFolded,
  labelsVisible,
}: {
  acc: JSX.Element[]
  depth: number
  item: Item
  pathname?: string | null
  isFolded?: boolean
  labelsVisible?: boolean
}): Array<JSX.Element> => {
  const checkPath = !!(item.path && pathname)
  const partialMatch = checkPath ? pathname.includes(item.path!) : false
  const leafActive =
    checkPath &&
    (item.matchPathPrefix
      ? pathname === item.path || pathname.startsWith(`${item.path!}/`)
      : pathname === item.path)

  if (item.items) {
    const visibleChildItems = item.items.filter(
      (child) => child.path && !child.disabled
    )
    const firstChildPath = visibleChildItems[0]?.path

    acc.push(
      <SideNavItem
        active={partialMatch}
        depth={depth}
        badgeLabel={item.badgeLabel}
        disabled={item.disabled}
        firstChildPath={firstChildPath}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={partialMatch}
        title={item.title}
        isFolded={isFolded}
        labelsVisible={labelsVisible}
      >
        <Stack
          component="ul"
          spacing={0}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: visibleChildItems,
            pathname,
            isFolded,
            labelsVisible,
          })}
        </Stack>
      </SideNavItem>
    )
  } else {
    acc.push(
      <SideNavItem
        active={leafActive}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        labelsVisible={labelsVisible}
        badgeLabel={item.badgeLabel}
        path={item.path}
        title={item.title}
        isFolded={isFolded}
      />
    )
  }

  return acc
}

interface SideNavSectionProps {
  items?: Item[]
  labelsVisible?: boolean
  pathname?: string | null
  subheader?: string
  isFolded?: boolean
}

export const SideNavSection: FC<SideNavSectionProps> = (props) => {
  const {
    items = [],
    labelsVisible = true,
    pathname,
    subheader = '',
    isFolded = false,
    ...other
  } = props
  // Le trait se pose au-dessus de la première entrée `dividerBefore` (premier niveau seulement).
  const dividerIndex = items.findIndex((item) => item.dividerBefore)
  const head = dividerIndex < 0 ? items : items.slice(0, dividerIndex)
  const tail = dividerIndex < 0 ? [] : items.slice(dividerIndex)

  return (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
      {...other}
    >
      {!isFolded && labelsVisible && subheader && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: SIDE_NAV_SECTION_TITLE_FONT_SIZE,
            fontWeight: 700,
            lineHeight: 1.66,
            ml: 1,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items: head, pathname, isFolded, labelsVisible })}
      {dividerIndex >= 0 ? (
        <Divider
          component="li"
          sx={{ borderColor: 'var(--nav-border-color)', my: 0.5 }}
        />
      ) : null}
      {renderItems({ items: tail, pathname, isFolded, labelsVisible })}
    </Stack>
  )
}
